import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_ENDPOINTS, apiRequest } from '../Email-Marketing/config/api.js';

// Replaces the Email-Marketing `useSavedSegmentsStore` zustand store.

export const fetchSegments = createAsyncThunk(
  'emailSavedSegments/fetchSegments',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.sourceTable) queryParams.append('sourceTable', filters.sourceTable);
      if (filters.tags?.length) queryParams.append('tags', filters.tags.join(','));

      const data = await apiRequest(`${API_ENDPOINTS.SAVED_SEGMENTS.BASE}?${queryParams}`);
      return data.data;
    } catch (error) {
      console.error('Error fetching segments:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const getSegmentById = createAsyncThunk(
  'emailSavedSegments/getSegmentById',
  async (id) => {
    const data = await apiRequest(API_ENDPOINTS.SAVED_SEGMENTS.BY_ID(id));
    return data.data;
  }
);

export const createSegment = createAsyncThunk(
  'emailSavedSegments/createSegment',
  async (segmentData, { rejectWithValue }) => {
    try {
      const data = await apiRequest(API_ENDPOINTS.SAVED_SEGMENTS.BASE, {
        method: 'POST',
        body: JSON.stringify(segmentData),
      });
      return data.data;
    } catch (error) {
      console.error('Error creating segment:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateSegment = createAsyncThunk(
  'emailSavedSegments/updateSegment',
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const data = await apiRequest(API_ENDPOINTS.SAVED_SEGMENTS.BY_ID(id), {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      return { id, segment: data.data };
    } catch (error) {
      console.error('Error updating segment:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const deleteSegment = createAsyncThunk(
  'emailSavedSegments/deleteSegment',
  async (id, { rejectWithValue }) => {
    try {
      const data = await apiRequest(API_ENDPOINTS.SAVED_SEGMENTS.BY_ID(id), {
        method: 'DELETE',
      });
      return { id, data };
    } catch (error) {
      console.error('Error deleting segment:', error);
      return rejectWithValue(error.message);
    }
  }
);

export const getSegmentContacts = createAsyncThunk(
  'emailSavedSegments/getSegmentContacts',
  async ({ id, limit = 1000, offset = 0 }) => {
    const data = await apiRequest(
      `${API_ENDPOINTS.SAVED_SEGMENTS.CONTACTS(id)}?limit=${limit}&offset=${offset}`
    );
    return data.data;
  }
);

export const previewSegment = createAsyncThunk(
  'emailSavedSegments/previewSegment',
  async (segmentData) => {
    const data = await apiRequest(API_ENDPOINTS.SAVED_SEGMENTS.PREVIEW, {
      method: 'POST',
      body: JSON.stringify(segmentData),
    });
    return data.data;
  }
);

// Dropdown/checkbox options for the Company tab, sourced live from RDS.
export const fetchCompanyFieldOptions = createAsyncThunk(
  'emailSavedSegments/fetchCompanyFieldOptions',
  async () => {
    const data = await apiRequest(API_ENDPOINTS.SAVED_SEGMENTS.COMPANY_FIELD_OPTIONS);
    return data.data;
  }
);

export const getSegmentStats = createAsyncThunk(
  'emailSavedSegments/getSegmentStats',
  async () => {
    const data = await apiRequest(API_ENDPOINTS.SAVED_SEGMENTS.STATS);
    return data.data;
  }
);

const initialState = {
  segments: [],
  loading: false,
  error: null,
};

const emailSavedSegmentsSlice = createSlice({
  name: 'emailSavedSegments',
  initialState,
  reducers: {
    clearSegmentsError: (state) => {
      state.error = null;
    },
    resetSegments: () => initialState,
  },
  extraReducers: (builder) => {
    const pending = (state) => {
      state.loading = true;
      state.error = null;
    };
    const rejected = (state, action) => {
      state.loading = false;
      state.error = action.payload;
    };
    builder
      .addCase(fetchSegments.pending, pending)
      .addCase(fetchSegments.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = action.payload;
      })
      .addCase(fetchSegments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.segments = [];
      })
      .addCase(createSegment.pending, pending)
      .addCase(createSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = [action.payload, ...state.segments];
      })
      .addCase(createSegment.rejected, rejected)
      .addCase(updateSegment.pending, pending)
      .addCase(updateSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = state.segments.map((s) =>
          s._id === action.payload.id ? action.payload.segment : s
        );
      })
      .addCase(updateSegment.rejected, rejected)
      .addCase(deleteSegment.pending, pending)
      .addCase(deleteSegment.fulfilled, (state, action) => {
        state.loading = false;
        state.segments = state.segments.filter((s) => s._id !== action.payload.id);
      })
      .addCase(deleteSegment.rejected, rejected);
  },
});

export const { clearSegmentsError, resetSegments } = emailSavedSegmentsSlice.actions;

export default emailSavedSegmentsSlice.reducer;
